import dayjs from 'dayjs';
import { instantToWallClock } from 'tools/date/site-time';

export type StartMode = 'now' | 'exact';
export type EndPreset = 'p1' | 'p2' | 'p3' | 'p7' | 'exact';

// pN se lee como "el pase dura N días", igual que el durationDays que reemplaza:
// vence al final del día (inicio + N - 1).
export const PRESET_DAYS: Record<Exclude<EndPreset, 'exact'>, number> = { p1: 1, p2: 2, p3: 3, p7: 7 };

export interface WindowInput {
    startMode: StartMode;
    startDate: string; // YYYY-MM-DD, sólo si startMode === 'exact'
    startTime: string; // HH:mm
    endPreset: EndPreset;
    endDate: string; // YYYY-MM-DD, sólo si endPreset === 'exact'
    endTime: string; // HH:mm
    offset: string; // offset ya resuelto: del sitio, o del dispositivo como fallback
}

export interface PassWindow {
    nbf: string; // RFC3339 con offset explícito
    exp: string;
    startDate: string; // día de inicio resuelto, para las etiquetas
    endDate: string;
}

const OFFSET_PATTERN = /^[+-]\d{2}:\d{2}$/;

// computeWindow se llama en cada render del controller (Task 7): un throw acá
// no falla ruidoso, deja la pantalla en blanco. El único llamador resuelve
// site?.timezone || deviceOffset(), que nunca devuelve vacío, así que un
// offset malformado hoy es un caso imposible — pero si algo lo rompe, mejor
// loguear y dejar que el backend rechace el RFC3339 inválido con 400 (ruidoso,
// visible) a convertir esto en pantalla en blanco.
const stamp = (date: string, clock: string, offset: string): string => {
    if (!OFFSET_PATTERN.test(offset)) {
        console.error(`pass-window.stamp: offset con formato inválido, se esperaba "±HH:MM" y se recibió "${offset}"`);
    }
    return `${date}T${clock}${offset}`;
};

export function computeWindow(input: WindowInput, now: dayjs.Dayjs = dayjs()): PassWindow {
    const { startMode, endPreset, offset } = input;

    let startDate: string;
    let startClock: string;
    if (startMode === 'now') {
        // Truncado al comienzo de la hora: nbf se arma con el reloj del teléfono pero
        // se compara contra el del servidor, y este producto ya tuvo que defenderse del
        // desfasaje de reloj del dispositivo. Redondear hacia abajo da hasta 59 minutos
        // de tolerancia sin afirmar ninguna hora de inicio en pantalla.
        const wall = instantToWallClock(now, offset);
        startDate = wall.date;
        startClock = wall.time;
    } else {
        startDate = input.startDate;
        startClock = `${input.startTime}:00`;
    }

    let endDate: string;
    let endClock: string;
    if (endPreset === 'exact') {
        endDate = input.endDate;
        endClock = `${input.endTime}:00`;
    } else {
        endDate = dayjs(startDate)
            .add(PRESET_DAYS[endPreset] - 1, 'day')
            .format('YYYY-MM-DD');
        endClock = '23:59:59';
    }

    return { nbf: stamp(startDate, startClock, offset), exp: stamp(endDate, endClock, offset), startDate, endDate };
}

// La única regla que queda, y es la misma que valida el backend en
// visitor_passes.go:88. Reemplaza al viejo toTime > fromTime, que rechazaba
// ventanas nocturnas multi-día perfectamente legítimas.
export function isWindowValid(w: PassWindow): boolean {
    return new Date(w.exp).getTime() > new Date(w.nbf).getTime();
}

// Ignora nbf a propósito: una ventana con nbf ya pasado y exp futuro está
// vigente y es válida (es el caso normal de startMode: 'now'). Su único
// trabajo es rechazar pases que nacen vencidos, no afirmar que arrancan ahora.
export function isWindowInFuture(w: PassWindow, now: dayjs.Dayjs = dayjs()): boolean {
    return new Date(w.exp).getTime() > now.valueOf();
}
